import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { TaskNotification } from '../interfaces/task-notification';

@Injectable({
  providedIn: 'root'
})
export class TaskNotificationService {
  private readonly _tasks = new BehaviorSubject<TaskNotification[]>([]);
  readonly tasks$ = this._tasks.asObservable();

  get tasks(): TaskNotification[] {
    return this._tasks.value;
  }

  createTask(title: string, message?: string): string {
    const id = this.generateId();

    const task: TaskNotification = {
      id,
      title,
      message,
      status: 'pending',
      createdAt: Date.now()
    };

    this._tasks.next([task, ...this._tasks.value]);
    return id;
  }

  updateTask(id: string, partial: Partial<TaskNotification>): void {
    const updated = this._tasks.value.map(task =>
      task.id === id ? { ...task, ...partial } : task
    );

    this._tasks.next(updated);
  }

  completeTask(id: string, message?: string): void {
    this.updateTask(id, {
      status: 'success',
      message,
      completedAt: Date.now()
    });
  }

  failTask(id: string, error?: string, message?: string): void {
    this.updateTask(id, {
      status: 'error',
      error,
      message,
      completedAt: Date.now()
    });
  }

  removeTask(id: string): void {
    this._tasks.next(this._tasks.value.filter(task => task.id !== id));
  }

  clearFinished(): void {
    this._tasks.next(
      this._tasks.value.filter(task => task.status === 'pending')
    );
  }

  trackObservable<T>(
    source$: Observable<T>,
    title: string,
    options?: {
      pendingMessage?: string;
      successMessage?: string;
      errorMessage?: string;
    }
  ): Observable<T> {
    const taskId = this.createTask(title, options?.pendingMessage);

    return source$.pipe(
      tap(() => {
        this.completeTask(taskId, options?.successMessage ?? 'Task completed successfully.');
      }),
      catchError((error: unknown) => {
        const parsedError = this.extractErrorMessage(error);
        this.failTask(
          taskId,
          parsedError,
          options?.errorMessage ?? 'Task failed.'
        );

        return throwError(() => error);
      }),
      finalize(() => {
      })
    );
  }

  private extractErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      return error.error?.message || error.message || 'HTTP request failed';
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'Unknown error';
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}